import React, { useState, useEffect, useRef } from 'react';
import { Box, breakpoints, Text, Header, Line, variables, space, Spinner } from '@generationtux/component-library';
import styled from 'styled-components';
import GalleryHeader from './GalleryHeader';
import GalleryDetails from './GalleryDetails';
import GalleryItem from './GalleryItem';
import GalleryFilter from './GalleryFilter';
import { bundles } from '../../../data/bundles';
import { products } from '../../../data/products';
import { isProductInListCategory, getCatalogNumber, sortProductRealPretty, deselectItem } from '../../../utils/utils';
import { useSpring, animated } from 'react-spring';
import SaveBox from '../SaveBox';
import useIntersect from '../../../hooks/IntersectionObserver';
import { listCategories } from '../../../data/listCategories';
import CategoryIcon from '../../shared/CategoryIcon';

// how many products to load
// use a common multiple of all
// column sizes (2,3,4) - 1 (for the empty)
const START_PRODUCT_COUNT = 11;

const IconHolder = styled(Box)`
  position: relative;
  svg {
    width: ${space[4]}px;
    height: ${space[4]}px;
  }
  &:after {
    content: '';
    width: 200%;
    height: 2px;
    background-color: ${props => variables(props).grayDark};
    transform: translate(-50%, -50%) rotate(-45deg);
    position: absolute;
    top: 50%;
    left: 50%;
    box-shadow: 0 1px 0px 1px white;
  }
`;

const BoxRemoveLink = styled(Box)`
  overflow: hidden;
  position: relative;
  z-index: 1;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:active {
    transform: scale(0.95);
  }
  > div:first-child {
    transform: ${props => (!props.activeItem ? 'scale(.97)' : 'scale(1)')};
  }
  ${props =>
    props.activeItem &&
    `
    cursor: pointer;
    @media(hover:hover){
      &:hover {
        ${`box-shadow: 0 0 0 1px ${variables(props).primary}, ${variables(props).shadows[3]};`}
        z-index: 20;
      }
    }
  `}
`;

const Grid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: ${space[1]}px;
  grid-row-gap: ${space[1]}px;
  @media (min-width: ${breakpoints['xs']}) {
    padding: 0;
  }
  @media (min-width: ${breakpoints['md']}) {
    padding: 0;
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (min-width: 1900px) {
    padding: 0;
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
`;

const isCatalogNumberSelected = (product, selectedCatalogNumbers, isBundle = false) => {
  const catalogNumber = getCatalogNumber(product);
  return selectedCatalogNumbers.filter(x => x.activeItem === catalogNumber).length > 0;
};

const Gallery = ({
  activeCollection,
  setActiveCollection,
  selectedCatalogNumbers,
  docBrownsTimeMachine,
  setSelectedCatalogNumbers,
  undo
}) => {
  const [productDetails, setProductDetails] = useState(null);
  const [modelToggle, setModelToggle] = useState(true);
  const [listedProducts, setListedProducts] = useState(
    products
      .filter(product => isProductInListCategory(activeCollection, product))
      .sort((a, b) => sortProductRealPretty(a, b))
      .slice(0, START_PRODUCT_COUNT)
  );
  const [colorFilter, setColorFilter] = useState([]);
  const [patternFilter, setPatternFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const galleryTop = useRef(null);
  const allProductsLoaded = !(
    listedProducts.length < products.filter(product => isProductInListCategory(activeCollection, product)).length
  );
  const animationProps = useSpring({ opacity: 1, from: { opacity: 0 } });
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [initialItem] = useState(selectedCatalogNumbers.filter( item => item.name === activeCollection)[0].activeItem, null);
  const [activeItemChanged, setActiveItemChanged] = useState(false)

  const activeBundleName = selectedCatalogNumbers
    .filter(item => item.name === 'Jacket & Pants')
    .map((item, i) => !!item.activeItem && item.bundleName)[0];

  const fetchMoreProducts = () => {
    setListedProducts(state => [
      ...state,
      ...products
        .filter(product => isProductInListCategory(activeCollection, product))
        .sort((a, b) => sortProductRealPretty(a, b))
        .slice(state.length, state.length + START_PRODUCT_COUNT),
    ]);
  };

  const [ref, entry] = useIntersect({ threshold: 0, rootMargin: '0px 0px 0px 0px' });

  if (entry.isIntersecting && !allProductsLoaded && !isLoadingProducts) {
    setIsLoadingProducts(true);
    fetchMoreProducts();
  }

  useEffect(() => {
    setListedProducts(
      products
        .filter(product => isProductInListCategory(activeCollection, product))
        .sort((a, b) => sortProductRealPretty(a, b))
        .slice(0, START_PRODUCT_COUNT)
    );
    setColorFilter([]);
    setPatternFilter([]);
    setCategoryFilter([]);
  }, [activeCollection]);

  useEffect(() => {
    let timer = setTimeout(() => {
      setIsLoadingProducts(false);
    }, 500);
    return () => {
      clearTimeout(timer);
    };
  }, [isLoadingProducts]);

  return (
    <animated.div style={animationProps} ref={galleryTop}>
      <Box display="flex" flexFlow="column">
        <Box>
          {productDetails ? (
            <GalleryDetails
              activeCollection={activeCollection}
              setActiveCollection={setActiveCollection}
              productDetails={productDetails}
              selectedCatalogNumbers={selectedCatalogNumbers}
              setProductDetails={setProductDetails}
              docBrownsTimeMachine={docBrownsTimeMachine}
              selected={isCatalogNumberSelected(
                productDetails.product,
                selectedCatalogNumbers,
                productDetails.isBundle
              )}
            />
          ) : (
            <>
              <GalleryHeader
                activeCollection={activeCollection}
                modelToggle={modelToggle}
                setModelToggle={setModelToggle}
                setActiveCollection={setActiveCollection}
              />

              {activeCollection !== 'Jacket & Pants' &&
                activeBundleName &&
                bundles
                  .filter(bundle => bundle.displayName.includes('RECOMMENDED') === true)
                  .filter(bundle => bundle.displayName.includes(activeBundleName) === true)
                  .map(bundle =>
                    bundle.products.map(
                      product =>
                        product.displayable &&
                        product.type.includes(activeCollection.substring(0, 3)) && (
                          <Box
                            display="flex"
                            justifyContent="center"
                            backgroundColor="grayLighter"
                            key={product.id}
                            py={[3, 3, 3, 3, 3, 4]}
                            px={[3, 3, 3, 4, 4, 5]}
                            mb={1}
                          >
                            <Box display="flex" justifyContent="center" alignItems="center" pr={[3, 3, 4, 4, 5, 5]}>
                              <Box>
                                <Header headline type={5} mb={1}>
                                  Recommended
                                </Header>
                                <Line headlineDivider my={[3, 3, 3, 3, 3, 3]} />
                                <Text size="sm" mb={2} textColor="grayDark">
                                  This item matches your jacket selection.
                                </Text>
                              </Box>
                            </Box>
                            <Box style={{ maxWidth: 160 }} mr={[0, 0, 0, 0, 4, 4]}>
                              <GalleryItem
                                setProductDetails={setProductDetails}
                                activeCollection={activeCollection}
                                setActiveCollection={setActiveCollection}
                                selectedCatalogNumbers={selectedCatalogNumbers}
                                docBrownsTimeMachine={docBrownsTimeMachine}
                                product={product}
                                selected={isCatalogNumberSelected(product, selectedCatalogNumbers)}
                                setActiveItemChanged={setActiveItemChanged}
                              />
                            </Box>
                          </Box>
                        )
                    )
                  )}

              <GalleryFilter
                activeCollection={activeCollection}
                isBundle={activeCollection === 'Jacket & Pants'}
                colorFilter={colorFilter}
                setColorFilter={setColorFilter}
                patternFilter={patternFilter}
                setPatternFilter={setPatternFilter}
                categoryFilter={categoryFilter}
                setCategoryFilter={setCategoryFilter}
              />

            <Grid>
              <>
                {selectedCatalogNumbers
                  .filter(item => item.name === activeCollection)
                  .map((item, i) => (
                    <BoxRemoveLink
                      key={i}
                      activeItem={item.activeItem}
                      backgroundColor={item.activeItem ? 'white' : 'primary'}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="stretch"
                      flexFlow="column"
                      style={{ minHeight: 180 }}
                      onTouchStart={()=>{}}
                      onClick={() => {
                        item.activeItem &&
                          setActiveItemChanged(true);
                          docBrownsTimeMachine(deselectItem(selectedCatalogNumbers, activeCollection), true, null);
                      }}
                    >
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          style={{ flexGrow: 1 }}
                          backgroundColor={activeCollection === 'Jacket & Pants' ? 'grayLighter' : 'white'}
                        >
                          <IconHolder>
                            {listCategories.map(
                              (category, i) =>
                                category.name === activeCollection && (
                                  <Box key={i}>
                                    <CategoryIcon category={category.category} color="grayDark" />
                                  </Box>
                                )
                            )}
                          </IconHolder>
                        </Box>

                        <Line lineColor={item.activeItem ? 'grayLighter' : 'primary'} />

                        <Box
                          p={2}
                          backgroundColor={item.activeItem ? 'white' : 'primary'}
                          style={{ minHeight: '48px' }}
                        >
                          <Text size="xs">None</Text>
                        </Box>
                      </BoxRemoveLink>
                    ))}

                {activeCollection === 'Jacket & Pants' && (
                  <>
                    {bundles
                      .filter(bundle => bundle.displayName.includes('RECOMMENDED') === false)
                      .filter(bundle => (colorFilter.length > 0 ? colorFilter.indexOf(bundle.color) > -1 : true))
                      .filter(bundle => (categoryFilter.length > 0 ? categoryFilter.indexOf(bundle.type) > -1 : true))
                      .map(bundle => (
                        <GalleryItem
                          key={bundle.bundleId}
                          setProductDetails={setProductDetails}
                          activeCollection={activeCollection}
                          setActiveCollection={setActiveCollection}
                          selectedCatalogNumbers={selectedCatalogNumbers}
                          docBrownsTimeMachine={docBrownsTimeMachine}
                          modelToggle={modelToggle}
                          product={bundle}
                          isBundle={true}
                          selected={isCatalogNumberSelected(bundle, selectedCatalogNumbers, true)}
                          initialItem={initialItem}
                          setActiveItemChanged={setActiveItemChanged}
                        />
                      ))}
                  </>
                )}

                  {activeCollection !== 'Jacket & Pants' && (
                    <>
                      {[...colorFilter, ...patternFilter, ...categoryFilter].length > 0 ? (
                        <>
                          {products
                            .filter(product => isProductInListCategory(activeCollection, product))
                            .filter(product =>
                              colorFilter.length > 0 ? colorFilter.indexOf(product.colorGroup) > -1 : true
                            )
                            .filter(product =>
                              patternFilter.length > 0 ? patternFilter.indexOf(product.pattern) > -1 : true
                            )
                            .filter(product => {
                              if (activeCollection === 'Shirt') {
                                return categoryFilter.length > 0 ? product.catalogName.includes('Pleated') : true;
                              }
                              if (activeCollection === 'Tie') {
                                return categoryFilter.length > 0
                                  ? categoryFilter.indexOf(product.attributePrimary) > -1 ||
                                      categoryFilter.indexOf(product.attributeSecondary) > -1
                                  : true;
                              }
                              if (activeCollection === 'Lapel Pin') {
                                return categoryFilter.length > 0
                                  ? categoryFilter.indexOf(product.attributePrimary) > -1 ||
                                      categoryFilter.indexOf(product.attributeSecondary) > -1 ||
                                      categoryFilter.indexOf(product.attributeTertiary) > -1
                                  : true;
                              }
                              if (activeCollection === 'Vest & Cummerbund') {
                                return categoryFilter.length > 0
                                  ? categoryFilter.indexOf(product.type) > -1 ||
                                      categoryFilter.indexOf(product.attributePrimary) > -1
                                  : true;
                              }
                              return true;
                            })
                            .sort((a, b) => sortProductRealPretty(a, b))
                            .map(product => (
                              <GalleryItem
                                key={product.id}
                                setProductDetails={setProductDetails}
                                activeCollection={activeCollection}
                                setActiveCollection={setActiveCollection}
                                selectedCatalogNumbers={selectedCatalogNumbers}
                                docBrownsTimeMachine={docBrownsTimeMachine}
                                product={product}
                                selected={isCatalogNumberSelected(product, selectedCatalogNumbers)}
                                setActiveItemChanged={setActiveItemChanged}
                              />
                            ))}
                        </>
                      ) : (
                      <>
                        {listedProducts.map(product => (
                          <GalleryItem
                            key={product.id}
                            setProductDetails={setProductDetails}
                            activeCollection={activeCollection}
                            setActiveCollection={setActiveCollection}
                            selectedCatalogNumbers={selectedCatalogNumbers}
                            docBrownsTimeMachine={docBrownsTimeMachine}
                            product={product}
                            selected={isCatalogNumberSelected(product, selectedCatalogNumbers)}
                            initialItem={initialItem}
                            setActiveItemChanged={setActiveItemChanged}
                          />
                        ))}
                      </>
                    )}
                  </>
                )}
              </>
            </Grid>
            {!allProductsLoaded && colorFilter.length < 1 && patternFilter.length < 1 && categoryFilter.length < 1 && (
              <div ref={ref} style={{ width: '100%' }}>
                <Spinner type="minimal" />
              </div>
            )}
            <SaveBox
              productDetails={productDetails}
              setProductDetails={setProductDetails}
              activeCollection={activeCollection}
              setActiveCollection={setActiveCollection}
              selectedCatalogNumbers={selectedCatalogNumbers}
              docBrownsTimeMachine={docBrownsTimeMachine}
              selected={activeItemChanged}
              undo={undo}
            />
          </>
        )}
      </Box>
      </Box>
    </animated.div>
  );
};

export default Gallery;
